import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	ITextMessagingComponentProps,
	TextMessagingViewModelKey,
	UserSessionViewModelKey,
} from '../../../../../../../../models/AppState';
import { IEventLoggingComponentProps } from '../../../../../../../../models/Logging';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../../../../components-v2/Dropdown';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { EditItemPenIcon } from '../../../../../../../components/svgs/icons/EditItemPenIcon';
import { PhoneIcon } from '../../../../../../../components/svgs/icons/PhoneIcon';
import { TextingIcon } from '../../../../../../../components/svgs/icons/TextingIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';
import { PopoverType, TinyPopover } from '../../../../../../../components/TinyPopover';
import { CssSelectors } from '../../../../../../../styles/styles';
import { PhoneNumberForm } from './PhoneNumberForm';

const { colors, spacing } = THEME;

const styleSheet = StyleSheet.create({
	phoneCard: {
		display: 'flex',
		flexDirection: 'row',
		gap: spacing.sm,
		alignItems: 'flex-start',
		minHeight: 20,
	},
	phoneCardIcon: {
		paddingTop: spacing.xs,
	},
	phoneCardContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: spacing.sm,
		width: '100%',
	},
	phoneCardContentRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		minHeight: 32,
		justifyContent: 'space-between',
		width: '100%',
		':hover': {
			':nth-child(1n) .actions': {
				display: 'flex',
			},
		},
		':nth-child(1n) .visible': {
			display: 'flex',
		},
	},
	phoneCardLabel: {
		fontSize: 12,
		fontWeight: 600,
		color: colors.text.light,
		margin: 0,
		padding: 0,
	},
	phoneCardValue: {
		margin: 0,
		maxWidth: 260,
		width: 260,
		display: 'inline-block',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		color: colors.text.main,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'none',
		},
	},
	phoneCardActionButton: {
		paddingTop: 0,
	},
	phoneCardDeleteButton: {
		display: 'flex',
		userSelect: 'none',
		alignItems: 'center',
		gap: '8px',
		borderRadius: '2px',
		padding: 0,
		fontSize: '0.875rem',
		outline: 'none',
		color: 'rgb(118, 118, 118)',
	},
	deleteItemConfirmationArrow: {
		...CssSelectors.allChildren(
			{
				':after': {
					borderLeft: '25px solid transparent',
					borderTop: '11px solid var(--color-success-light)',
					content: "' '",
					left: -24,
					position: 'absolute',
					top: -10,
					zIndex: -1,
				},
				borderLeftWidth: '22px !important',
				borderRight: 'none !important',
				left: 'auto !important',
				right: 10,
				width: 0,
			},
			'div:first-child'
		),
	},
	deleteItemConfirmationContainer: {
		borderBottomRightRadius: 0,
		maxWidth: 310,
		boxSizing: 'border-box',
		padding: spacing.sm,
		position: 'relative',
	},
	closeDeleteConfirmationContainer: {
		height: 14,
		position: 'absolute',
		right: 8,
		top: 5,
		width: 14,
		...CssSelectors.allChildren({
			height: '100%',
			width: '100%',
		}),
	},
});

interface PhoneNumberCardProps
	extends ITextMessagingComponentProps,
		IErrorMessageComponentProps,
		IEventLoggingComponentProps,
		IFullscreenModalComponentProps {
	phoneNumber: Api.IPhoneNumber;
	isPrimary?: boolean;
	contact: Api.ContactViewModel;
	onPhoneNumberChanged: (phoneNumber: Api.IPhoneNumber) => void;
	onDelete: () => void;
}

const _PhoneNumberCard = ({
	phoneNumber,
	onPhoneNumberChanged,
	onDelete,
	textMessaging,
	errorMessages,
	logApiError,
	fullscreenModal,
	contact,
}: PhoneNumberCardProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const [phoneNumberValue, setPhoneNumberValue] = useState<Api.IPhoneNumber>(phoneNumber);
	const [isDeleting, setIsDeleting] = useState(false);
	const history = useHistory();
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean | null>(null);
	const displayedPhoneNumber = phoneNumber?.metadata?.standard ?? phoneNumber?.value;

	// Sync local state with prop when it changes (e.g., after editing via Edit Profile)
	useEffect(() => {
		setPhoneNumberValue(phoneNumber);
	}, [phoneNumber]);

	const updatePhoneNumber = () => {
		setIsEditing(false);
		onPhoneNumberChanged({ ...phoneNumberValue });
	};

	const onSendTextClicked = async () => {
		try {
			if (!textMessaging.phoneNumberOrder) {
				history.push(`/texting`);
				return;
			}

			const conversation = await textMessaging.createConversation([phoneNumber.value]);

			conversation.updateRecipients({
				...contact.toJs(),
				selectedPhoneNumber: phoneNumber,
			});

			fullscreenModal.dismissModal();

			history.push(`/texting/${textMessaging.id}/${conversation.id}`);
		} catch (err) {
			errorMessages.pushApiError(err);

			logApiError('CreateConversationFromContact-Error', err);
		}
	};

	if (!phoneNumber) {
		return null;
	}

	if (isEditing) {
		return (
			<InlineValueEditor
				autoComplete='off'
				autoFocus={true}
				compactLayout={true}
				id='entity-info-bullet-list-editor-input'
				onCancelButtonClicked={() => {
					setPhoneNumberValue(phoneNumber);
					setIsEditing(false);
				}}
				onKeyDown={ev => {
					if (ev.keyCode === 13) {
						updatePhoneNumber();
					}
				}}
				onRenderInput={() => <PhoneNumberForm phone={phoneNumberValue} onChange={setPhoneNumberValue} />}
				onSaveButtonClicked={updatePhoneNumber}
				value={phoneNumberValue.value || ''}
			/>
		);
	}

	return (
		<div className={css(styleSheet.phoneCard)}>
			<div className={css(styleSheet.phoneCardIcon)}>
				<PhoneIcon fill={THEME.colors.border.main} width={18} height={18} />
			</div>
			<div className={css(styleSheet.phoneCardContent)}>
				<div className={css(styleSheet.phoneCardContentRow)}>
					<div>
						<p className={css(styleSheet.phoneCardLabel)}>{phoneNumber.label}</p>
						<p className={css(styleSheet.phoneCardValue)}>{displayedPhoneNumber}</p>
					</div>

					<div>
						<Button kind='icon' onClick={onSendTextClicked} className={css(styleSheet.phoneCardActionButton)}>
							<TextingIcon fill={THEME.colors.primary.main} />
						</Button>
					</div>

					<div>
						<Dropdown onOpenChange={open => setIsDropdownOpen(open)} open={isDropdownOpen || isDeleting}>
							<DropdownEllipsisTrigger />
							<DropdownContent>
								<DropdownItem onClick={() => setIsEditing(true)}>
									<EditItemPenIcon /> Edit
								</DropdownItem>
								<DropdownItem onClick={() => setIsDeleting(true)}>
									<TinyPopover
										align='end'
										anchor={
											<div className={css(styleSheet.phoneCardDeleteButton)} onClick={() => setIsDeleting(true)}>
												<TrashIcon />
												Delete
											</div>
										}
										arrow={{
											arrowSize: 10,
										}}
										arrowStyles={[styleSheet.deleteItemConfirmationArrow]}
										dismissOnOutsideAction={true}
										isOpen={isDeleting}
										onRequestClose={() => setIsDeleting(false)}
										placement={['top']}
										type={PopoverType.blue}
									>
										<div className={css(styleSheet.deleteItemConfirmationContainer)}>
											<div
												className={css(styleSheet.closeDeleteConfirmationContainer)}
												onClick={() => setIsDeleting(false)}
											>
												<XIcon color={THEME.colors.text.light} height={12} width={12} />
											</div>
											<p>Do you want to delete the phone number {phoneNumber?.metadata?.standard}?</p>
											<Button size='small' onClick={onDelete}>
												Delete
											</Button>
										</div>
									</TinyPopover>
								</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</div>
				</div>
			</div>
		</div>
	);
};

const PhoneNumberCardAsObserver = observer(_PhoneNumberCard);
export const PhoneNumberCard = inject(
	UserSessionViewModelKey,
	TextMessagingViewModelKey,
	FullScreenModalViewModelKey,
	ErrorMessagesViewModelKey
)(PhoneNumberCardAsObserver);
